import { Trans, useLingui } from '@lingui/react/macro';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SwapCallsOutlined from '@mui/icons-material/SwapCallsOutlined';
import VerticalAlignBottomRounded from '@mui/icons-material/VerticalAlignBottomRounded';
import VerticalAlignTopRounded from '@mui/icons-material/VerticalAlignTopRounded';
import { Box, ClickAwayListener, Typography } from '@mui/material';
import { type SxProps, styled, type Theme } from '@mui/material/styles';
import { type KeyboardEvent, memo, useCallback, useRef, useState } from 'react';
import { Button } from 'ui-kit';
import { useBridgeCheckoutStore } from '../../../providers/fun_bridge/bridge_checkout_store.ts';
import { HoverMenu, hoverMenuItemTabIndex } from '../hover_menu.tsx';
import {
  NavMenuItemButton,
  navMenuItemDescriptionSx,
  navMenuItemHeaderSx,
  navMenuItemIconSx,
} from '../nav_hover_group/nav_menu_item_styles.ts';
import { useHoverMenu } from '../use_hover_menu.ts';

const MENU_WIDTH = 240;
const PANEL_PADDING = 'var(--mui-spacing)';
const PANEL_ITEM_GAP = 'calc(0.5 * var(--mui-spacing))';

const TriggerRoot = styled(Box)({
  position: 'relative',
  display: 'inline-flex',
});

const menuCardSx = {
  width: MENU_WIDTH,
  maxWidth: 'calc(100vw - 32px)',
  display: 'flex',
  flexDirection: 'column',
  gap: PANEL_ITEM_GAP,
  p: PANEL_PADDING,
  boxSizing: 'border-box',
} as const;

interface FundAccountMenuProps {
  canBridge: boolean;
  depositLabel: string;
  canWithdraw: boolean;
  onDeposit: () => void;
  onWithdraw: () => void;
  sx?: SxProps<Theme>;
}

export const FundAccountMenu = memo(function FundAccountMenu({
  canBridge,
  depositLabel,
  canWithdraw,
  onDeposit,
  onWithdraw,
  sx,
}: FundAccountMenuProps) {
  const { t } = useLingui();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const requestCheckout = useBridgeCheckoutStore((state) => state.requestCheckout);

  const setTriggerRef = useCallback((node: HTMLButtonElement | null) => {
    triggerRef.current = node;
    setAnchorEl(node);
  }, []);

  const { open, mounted, menuId, handleToggle, handleClose, handleEscape } = useHoverMenu();

  const handleDepositClick = useCallback(() => {
    handleClose();
    onDeposit();
  }, [handleClose, onDeposit]);

  const handleWithdrawClick = useCallback(() => {
    handleClose();
    onWithdraw();
  }, [handleClose, onWithdraw]);

  const handleBridgeClick = useCallback(() => {
    handleClose();
    requestCheckout({ source: 'fund_menu' });
  }, [handleClose, requestCheckout]);

  const handleClickAway = useCallback(() => {
    if (open) handleClose();
  }, [handleClose, open]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleEscape(triggerRef.current);
      }
    },
    [handleEscape]
  );

  const withdrawLabel = t({
    comment: 'Withdraw action in fund account menu',
    message: 'Withdraw',
  });

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <TriggerRoot sx={sx} onKeyDown={handleKeyDown}>
        <Button
          ref={setTriggerRef}
          onClick={handleToggle}
          variant="outlined"
          size="medium"
          endIcon={
            <ExpandMoreIcon
              sx={{
                fontSize: '1.1rem',
                transition: 'transform 150ms',
                transform: `rotate(${open ? 180 : 0}deg)`,
              }}
            />
          }
          aria-haspopup="true"
          aria-expanded={open}
          aria-controls={menuId}
        >
          {depositLabel}
        </Button>

        <HoverMenu
          id={menuId}
          open={open}
          mounted={mounted}
          anchorEl={anchorEl}
          width={MENU_WIDTH}
          cardSx={menuCardSx}
          onEscape={() => handleEscape(triggerRef.current)}
        >
          <NavMenuItemButton
            isHighlighted={hoveredKey === 'deposit'}
            onMouseEnter={() => setHoveredKey('deposit')}
            onFocus={() => setHoveredKey('deposit')}
            onClick={handleDepositClick}
            tabIndex={hoverMenuItemTabIndex(open)}
            disableRipple
          >
            <Typography component="span" sx={navMenuItemHeaderSx}>
              <VerticalAlignBottomRounded sx={navMenuItemIconSx} />
              {depositLabel}
            </Typography>
            <Typography sx={navMenuItemDescriptionSx}>
              <Trans comment="Fund account menu deposit description">Add funds to your account</Trans>
            </Typography>
          </NavMenuItemButton>

          {canWithdraw ? (
            <NavMenuItemButton
              isHighlighted={hoveredKey === 'withdraw'}
              onMouseEnter={() => setHoveredKey('withdraw')}
              onFocus={() => setHoveredKey('withdraw')}
              onClick={handleWithdrawClick}
              tabIndex={hoverMenuItemTabIndex(open)}
              disableRipple
            >
              <Typography component="span" sx={navMenuItemHeaderSx}>
                <VerticalAlignTopRounded sx={navMenuItemIconSx} />
                {withdrawLabel}
              </Typography>
              <Typography sx={navMenuItemDescriptionSx}>
                <Trans comment="Fund account menu withdraw description">Transfer to your wallet</Trans>
              </Typography>
            </NavMenuItemButton>
          ) : null}

          <NavMenuItemButton
            isHighlighted={hoveredKey === 'bridge'}
            onMouseEnter={() => setHoveredKey('bridge')}
            onFocus={() => setHoveredKey('bridge')}
            onClick={handleBridgeClick}
            tabIndex={hoverMenuItemTabIndex(open)}
            disabled={!canBridge}
            disableRipple
            sx={!canBridge ? { opacity: 0.4 } : undefined}
          >
            <Typography component="span" sx={navMenuItemHeaderSx}>
              <SwapCallsOutlined sx={navMenuItemIconSx} />
              <Trans comment="Fund account menu bridge label">Bridge</Trans>
            </Typography>
            <Typography sx={navMenuItemDescriptionSx}>
              <Trans comment="Fund account menu bridge description">Move assets across chains</Trans>
            </Typography>
          </NavMenuItemButton>
        </HoverMenu>
      </TriggerRoot>
    </ClickAwayListener>
  );
});
